import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import {
  NavigateNext,
  ThumbUp,
  ThumbDown,
  ArrowBack,
  ArrowForward,
  Home,
  Print,
} from '@mui/icons-material';
import { getArticleById, getCategoryById, getRelatedArticles, getAllArticles } from '../data/helpContent';

const HelpArticle = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Article Not Found
          </Typography>
          <Typography color="text.secondary" paragraph>
            The help article you're looking for doesn't exist.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/help')}
            startIcon={<Home />}
          >
            Back to Help Center
          </Button>
        </Paper>
      </Container>
    );
  }

  const category = getCategoryById(article.categoryId);
  const relatedArticles = getRelatedArticles(articleId);
  const allArticles = getAllArticles();
  const currentIndex = allArticles.findIndex(a => a.id === articleId);
  const previousArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;

  const handleFeedback = (helpful) => {
    // In a real implementation, this would send feedback to analytics
    alert(helpful ? 'Thank you for your feedback!' : 'We\'re sorry this wasn\'t helpful. We\'ll work to improve it.');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/help')}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Help Center
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/help/category/${article.categoryId}`)}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            {category?.title}
          </Link>
          <Typography variant="body2" color="text.primary">
            {article.title}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 4, mb: 3 }}>
              {/* Article Header */}
              <Box sx={{ mb: 4 }}>
                <Chip
                  label={category?.title}
                  color="primary"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  {article.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary' }}>
                  <Typography variant="body2">
                    Last updated: {new Date(article.lastUpdated).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">•</Typography>
                  <IconButton size="small" onClick={handlePrint} title="Print article">
                    <Print fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Article Content */}
              <Box
                sx={{
                  '& h1': {
                    fontSize: '2rem',
                    fontWeight: 600,
                    mt: 4,
                    mb: 2,
                  },
                  '& h2': {
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mt: 3,
                    mb: 2,
                  },
                  '& h3': {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    mt: 2,
                    mb: 1,
                  },
                  '& p': {
                    mb: 2,
                    lineHeight: 1.7,
                  },
                  '& ul, & ol': {
                    mb: 2,
                    pl: 3,
                  },
                  '& li': {
                    mb: 1,
                    lineHeight: 1.6,
                  },
                  '& code': {
                    backgroundColor: 'action.hover',
                    padding: '2px 6px',
                    borderRadius: 1,
                    fontSize: '0.9em',
                    fontFamily: 'monospace',
                  },
                  '& pre': {
                    backgroundColor: 'action.hover',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    mb: 2,
                  },
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    mb: 2,
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                    textAlign: 'left',
                  },
                  '& th': {
                    backgroundColor: 'action.hover',
                    fontWeight: 600,
                  },
                }}
              >
                <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  {article.content}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Feedback */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Was this article helpful?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ThumbUp />}
                    onClick={() => handleFeedback(true)}
                  >
                    Yes, this helped
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ThumbDown />}
                    onClick={() => handleFeedback(false)}
                  >
                    No, I need more help
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Previous/Next Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              {previousArticle ? (
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(`/help/article/${previousArticle.id}`)}
                  sx={{ flex: 1 }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="caption" display="block">
                      Previous
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {previousArticle.title}
                    </Typography>
                  </Box>
                </Button>
              ) : (
                <Box sx={{ flex: 1 }} />
              )}

              {nextArticle ? (
                <Button
                  variant="outlined"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate(`/help/article/${nextArticle.id}`)}
                  sx={{ flex: 1 }}
                >
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" display="block">
                      Next
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {nextArticle.title}
                    </Typography>
                  </Box>
                </Button>
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
            </Box>
          </Box>

          {/* Sidebar */}
          <Box sx={{ width: 300, display: { xs: 'none', md: 'block' } }}>
            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Related Articles
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/help/article/${related.id}`)}
                        sx={{
                          textAlign: 'left',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {related.title}
                      </Link>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Quick Links */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Links
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/help')}
                    sx={{
                      textAlign: 'left',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Help Center Home
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate(`/help/category/${article.categoryId}`)}
                    sx={{
                      textAlign: 'left',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    All {category?.title} Articles
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    href="mailto:support@paceestate.com"
                    sx={{
                      textAlign: 'left',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Contact Support
                  </Link>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default HelpArticle;
